import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import { format, addDays, endOfMonth, differenceInCalendarDays } from 'date-fns';
import style from './date-range.module.scss';

// Popover is ~600px tall (custom header + two months × ~340px each
// + Apply/Cancel footer). Use a conservative estimate so the
// placement flip triggers slightly early — better to open upward
// with a bit of extra space than to flip late and have the
// popover's tail clipped.
const POPOVER_HEIGHT_ESTIMATE = 600;

// Date-range field that collapses two separate "From / To" pickers
// into one clean control with a single popover. Preserves the
// backend contract by emitting two callbacks (onStartChange,
// onEndChange) with 'yyyy-MM-dd' strings — same format PLDatePicker
// uses — so CampaignContext storage is unchanged.
//
// UX features:
//  • One popup, two months shown side-by-side (native react-datepicker
//    `monthsShown` — no extra deps)
//  • Range highlight as user hovers between start/end
//  • Quick preset sidebar (Next 7/30 days, This month) — one click
//    for the common campaigns
//  • Live duration badge ("17 days") tells the user what they picked
//  • `minDate` support — typically `new Date()` so campaigns can't
//    start in the past
function DateRangeField({
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  placeholder = 'Select date range',
  minDate,
  maxDate,
}) {
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  // 'bottom' (below trigger) or 'top' (above). Decided when popover
  // opens, by comparing available space below/above the trigger.
  const [placement, setPlacement] = useState('bottom');

  // Staged selection — edits happen locally inside the popover and
  // are only committed to the parent when the user clicks Apply.
  // This prevents accidental saves on preset click or mid-drag, and
  // lets the user Cancel without disturbing the form state.
  const [draftStart, setDraftStart] = useState(startValue || null);
  const [draftEnd, setDraftEnd] = useState(endValue || null);

  // Sync draft with incoming props when popover opens (covers external
  // resets and edit-flow hydration).
  useEffect(() => {
    if (open) {
      setDraftStart(startValue || null);
      setDraftEnd(endValue || null);
    }
  }, [open, startValue, endValue]);

  const startDate = draftStart ? new Date(draftStart) : null;
  const endDate = draftEnd ? new Date(draftEnd) : null;

  // Close popup on outside click + ESC
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = e => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Return focus to the trigger when the popover closes so keyboard
  // navigation isn't orphaned. Also preserves ESC → back-to-trigger UX.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !open && triggerRef.current) {
      triggerRef.current.focus();
    }
    wasOpen.current = open;
  }, [open]);

  // Decide top-vs-bottom placement before the popover paints so the
  // user never sees it flash in the wrong spot. Measures available
  // space below the trigger; if it's less than the popover needs AND
  // there's more room above, open upward.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    if (spaceBelow < POPOVER_HEIGHT_ESTIMATE && spaceAbove > spaceBelow) {
      setPlacement('top');
    } else {
      setPlacement('bottom');
    }
  }, [open]);

  const toStr = d => (d ? format(d, 'yyyy-MM-dd') : null);

  // Range selections and presets only mutate the draft state —
  // users explicitly confirm via Apply (or discard via Cancel).
  const handleRangeChange = ([newStart, newEnd]) => {
    setDraftStart(toStr(newStart));
    setDraftEnd(toStr(newEnd));
  };

  const handleApply = () => {
    onStartChange(draftStart);
    onEndChange(draftEnd);
    setOpen(false);
  };

  const handleCancel = () => {
    // Discard the draft — revert to whatever the parent's current
    // value is. The useEffect above will re-sync on next open.
    setDraftStart(startValue || null);
    setDraftEnd(endValue || null);
    setOpen(false);
  };

  // Quick presets. Each sets both start + end in one action.
  // Not memoized: recomputed per render so "today" stays fresh across
  // midnight (previously `useMemo([])` captured `today` once and went
  // stale). The calculation is cheap — five Date arithmetic ops.
  const today = new Date();
  const presets = [
    {
      key: 'next7',
      label: 'Next 7 days',
      start: toStr(today),
      end: toStr(addDays(today, 6)),
    },
    {
      key: 'next14',
      label: 'Next 14 days',
      start: toStr(today),
      end: toStr(addDays(today, 13)),
    },
    {
      key: 'next30',
      label: 'Next 30 days',
      start: toStr(today),
      end: toStr(addDays(today, 29)),
    },
    {
      // "This month" = today → end of this month. Starting from the
      // 1st would include past dates that are disabled by minDate,
      // confusing users. Starting today is the only useful campaign
      // semantic.
      key: 'thisMonth',
      label: 'Rest of this month',
      start: toStr(today),
      end: toStr(endOfMonth(today)),
    },
    {
      key: 'next90',
      label: 'Next 90 days',
      start: toStr(today),
      end: toStr(addDays(today, 89)),
    },
  ];

  // Presets stage the selection so the user can review in the
  // calendar before confirming with Apply.
  const applyPreset = preset => {
    setDraftStart(preset.start);
    setDraftEnd(preset.end);
  };

  // Highlight a preset as active when the DRAFT matches its range,
  // so picking "Next 7 days" gets visually highlighted immediately.
  const activePresetKey = presets.find(p => p.start === draftStart && p.end === draftEnd)?.key;

  // Trigger display uses the COMMITTED values (props), not the draft.
  // This keeps the closed field showing the last-saved range while
  // the user is mid-edit in the popover.
  const committedStart = startValue ? new Date(startValue) : null;
  const committedEnd = endValue ? new Date(endValue) : null;
  const display = (() => {
    if (!committedStart && !committedEnd) return null;
    if (committedStart && !committedEnd) return format(committedStart, 'MMM d, yyyy');
    return {
      start: format(committedStart, 'MMM d'),
      end: format(committedEnd, 'MMM d, yyyy'),
    };
  })();

  // Duration badge shows committed range
  const duration =
    committedStart && committedEnd
      ? differenceInCalendarDays(committedEnd, committedStart) + 1
      : null;

  // Apply button is only enabled when both ends are set in the draft
  const canApply = !!draftStart && !!draftEnd;

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <button
        ref={triggerRef}
        type="button"
        className={`${style.trigger} ${open ? style.open : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className={style.value}>
          {!display && <span className={style.placeholder}>{placeholder}</span>}
          {typeof display === 'string' && <span>{display}</span>}
          {display && typeof display === 'object' && (
            <>
              <span>{display.start}</span>
              <span className={style.dash}>→</span>
              <span>{display.end}</span>
            </>
          )}
        </span>
        {duration != null && (
          <span className={style.duration}>
            {duration} {duration === 1 ? 'day' : 'days'}
          </span>
        )}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img className={style.icon} src="/images/calendar.svg" alt="" aria-hidden="true" />
      </button>

      {open && (
        <div
          className={`${style.popover} ${placement === 'top' ? style.popoverTop : ''}`}
          role="dialog"
          aria-label="Date range picker"
        >
          <div className={style.presets}>
            <span className={style.presetsHeader}>Quick select</span>
            {presets.map(p => (
              <button
                key={p.key}
                type="button"
                className={`${style.preset} ${activePresetKey === p.key ? style.presetActive : ''}`}
                onClick={() => applyPreset(p)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className={style.calendarWrap}>
            <div className={style.customHeader}>
              <span className={style.customTitle}>Custom range</span>
              <div className={style.customChips}>
                <span
                  className={`${style.chip} ${startDate && !endDate ? style.chipActive : ''} ${
                    !startDate ? style.chipEmpty : ''
                  }`}
                >
                  <span className={style.chipLabel}>From</span>
                  <span className={style.chipValue}>
                    {startDate ? format(startDate, 'MMM d, yyyy') : '—'}
                  </span>
                </span>
                <span className={style.chipDivider} aria-hidden="true">
                  →
                </span>
                <span className={`${style.chip} ${!endDate ? style.chipEmpty : ''}`}>
                  <span className={style.chipLabel}>To</span>
                  <span className={style.chipValue}>
                    {endDate ? format(endDate, 'MMM d, yyyy') : '—'}
                  </span>
                </span>
              </div>
              <span className={style.customHint}>
                {!startDate && 'Pick a start date'}
                {startDate && !endDate && 'Now pick an end date'}
                {startDate && endDate && 'Click a date to change the range'}
              </span>
            </div>
            <DatePicker
              selected={startDate}
              onChange={handleRangeChange}
              startDate={startDate}
              endDate={endDate}
              selectsRange
              inline
              monthsShown={2}
              minDate={minDate}
              maxDate={maxDate}
              // Fully custom header — the default header's arrow CSS
              // is brittle (fights between .react-datepicker-icon
              // width:0 + library's absolute offsets). Rendering our
              // own header sidesteps the whole mess and gives us
              // proper SVG chevrons we can style like any button.
              renderCustomHeader={({
                monthDate,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled,
                customHeaderCount,
              }) => (
                <div className={style.customMonthHeader}>
                  {/* Left arrow on the first visible month only */}
                  {customHeaderCount === 0 && (
                    <button
                      type="button"
                      className={style.monthNav}
                      onClick={decreaseMonth}
                      disabled={prevMonthButtonDisabled}
                      aria-label="Previous month"
                    >
                      <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
                        <path
                          d="M7.5 3L4.5 6L7.5 9"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                  <span className={style.monthTitle}>{format(monthDate, 'MMMM yyyy')}</span>
                  {/* Right arrow on the last visible month only */}
                  {customHeaderCount === 1 && (
                    <button
                      type="button"
                      className={`${style.monthNav} ${style.monthNavRight}`}
                      onClick={increaseMonth}
                      disabled={nextMonthButtonDisabled}
                      aria-label="Next month"
                    >
                      <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
                        <path
                          d="M4.5 3L7.5 6L4.5 9"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              )}
            />
            <div className={style.footer}>
              <button type="button" className={style.btnCancel} onClick={handleCancel}>
                Cancel
              </button>
              <button
                type="button"
                className={style.btnApply}
                onClick={handleApply}
                disabled={!canApply}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

DateRangeField.propTypes = {
  startValue: PropTypes.string,
  endValue: PropTypes.string,
  onStartChange: PropTypes.func.isRequired,
  onEndChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
};

export default DateRangeField;
